import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Printer, ArrowRight } from "lucide-react";
import { COMPANY } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const fieldClass =
  "w-full bg-transparent border-b border-white/20 focus:border-white text-white text-base py-3 placeholder:text-slate-500 transition-colors";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050914] text-white">
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-6">Contact</div>
          <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            Talk to us. <span className="text-silver">Anytime.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-6">Direct Lines</div>
            <ul className="space-y-8">
              <li className="flex items-start gap-5">
                <div className="w-12 h-12 border border-white/15 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">Phone</div>
                  <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone" className="font-display text-2xl text-white hover:text-slate-300">
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-12 h-12 border border-white/15 flex items-center justify-center shrink-0">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">Fax</div>
                  <span className="font-display text-2xl text-white">{COMPANY.fax}</span>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-12 h-12 border border-white/15 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">Email</div>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="font-display text-xl md:text-2xl text-white hover:text-slate-300 break-all">
                    {COMPANY.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-12 h-12 border border-white/15 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">Headquarters</div>
                  <div className="font-display text-xl md:text-2xl text-white">{COMPANY.address}</div>
                </div>
              </li>
            </ul>

            <div className="mt-10 aspect-video border border-white/10 overflow-hidden">
              <iframe
                title="Ferrer Auto Transport Location"
                src="https://www.google.com/maps?q=Woodbridge,+NJ+07095&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0B132B]/40 p-8 md:p-12"
            >
              <h2 className="font-display font-semibold text-3xl md:text-4xl text-white mb-8">Send us a message</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-x-12">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Full Name</label>
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className={fieldClass}
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Phone</label>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Email</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={fieldClass}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Message</label>
                  <textarea
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className={`${fieldClass} resize-none`}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-10 group bg-white text-[#050914] px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-slate-200 flex items-center gap-3 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
